import React from "react";
import type {
  LaunchableManagedSecretReference,
  LaunchableParameter,
} from "server";
import type { ManagedSecretSummary } from "server/devplane/managedSecrets/api";
import { ExpandableConfigurationSection } from "components/Launchables/DeployRedesign/shared/configuration/ExpandableConfigurationSection";
import {
  getLaunchableParameterEffectiveValue,
  launchableParameterSupportsManagedSecret,
  LaunchableParameterBindingForm,
  orderLaunchableParametersForPresentation,
} from "./LaunchableParameters";

interface ParameterConfigurationProps {
  parameters: LaunchableParameter[];
  values: Record<string, string>;
  secretSelections: Record<string, LaunchableManagedSecretReference>;
  availableSecrets: ManagedSecretSummary[];
  secretsLoading: boolean;
  organizationId: string;
  collectValues: boolean;
  hasEditedValues: boolean;
  missingRequiredCount: number;
  open: boolean;
  onToggle: () => void;
  onChange: (values: Record<string, string>) => void;
  onSecretSelectionsChange?: (
    selections: Record<string, LaunchableManagedSecretReference>,
  ) => void;
  disabled: boolean;
}

export const ParameterConfiguration: React.FC<ParameterConfigurationProps> = ({
  parameters,
  values,
  secretSelections,
  availableSecrets,
  secretsLoading,
  organizationId,
  collectValues,
  hasEditedValues,
  missingRequiredCount,
  open,
  onToggle,
  onChange,
  onSecretSelectionsChange,
  disabled,
}) => {
  if (parameters.length === 0) return null;

  const requiredCount = parameters.filter(
    (parameter) => parameter.required,
  ).length;
  const defaultedCount = parameters.filter(
    (parameter) => getLaunchableParameterEffectiveValue(parameter, {}) !== "",
  ).length;
  const summary = `${parameters.length} ${
    parameters.length === 1 ? "variable" : "variables"
  }${requiredCount > 0 ? ` · ${requiredCount} required` : ""}`;
  const collapsedDetails = orderLaunchableParametersForPresentation(
    parameters,
  ).map((parameter) => {
    const secretSelection = launchableParameterSupportsManagedSecret(parameter)
      ? secretSelections[parameter.name]
      : undefined;
    const selectedSecret = availableSecrets.find(
      (secret) => secret.id === secretSelection?.secretId,
    );
    const effectiveValue = getLaunchableParameterEffectiveValue(
      parameter,
      values,
    );
    const defaultValue = getLaunchableParameterEffectiveValue(parameter, {});
    return {
      label: parameter.name,
      value: secretSelection?.secretId
        ? `Secret: ${selectedSecret?.name || secretSelection.secretId} · ${
            secretSelection.versionId ? "fixed version" : "latest"
          }`
        : effectiveValue || "Not set",
      isUnset: effectiveValue === "" && !secretSelection?.secretId,
      masked:
        Boolean(secretSelection?.secretId) ||
        (effectiveValue !== "" && effectiveValue !== defaultValue),
      required: parameter.required,
    };
  });

  return (
    <ExpandableConfigurationSection
      id="launchable-parameters"
      title="Parameters"
      summary={summary}
      collapsedDetails={collapsedDetails}
      compactCollapsedDetails
      status={
        !collectValues
          ? "Sign in to edit"
          : missingRequiredCount > 0
            ? `${missingRequiredCount} required`
            : hasEditedValues
              ? undefined
              : defaultedCount > 0
                ? "Defaults"
                : "Optional"
      }
      statusTone={
        collectValues && missingRequiredCount > 0 ? "attention" : "neutral"
      }
      actionLabel={collectValues ? "Change" : "Review"}
      open={open}
      onToggle={onToggle}
    >
      {collectValues ? (
        <>
          <p className="px-5 pt-4 text-xs text-secondary">
            Passed as environment variables when the launchable starts.
          </p>
          <LaunchableParameterBindingForm
            parameters={parameters}
            values={values}
            onChange={onChange}
            secretSelections={secretSelections}
            onSecretSelectionsChange={onSecretSelectionsChange}
            availableSecrets={availableSecrets}
            secretsLoading={secretsLoading}
            organizationId={organizationId}
            disabled={disabled}
            hideHeader
          />
        </>
      ) : (
        <div className="px-5 py-4">
          <p className="text-xs text-secondary">
            You&apos;ll provide these values after signing in.
          </p>
        </div>
      )}
    </ExpandableConfigurationSection>
  );
};
